import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Tabs } from "@/components/ui/tabs";
import { useAuthStore } from "@/stores/auth.store";
import { useMediaQuery } from "@/lib/use-media-query";
import { useLineMachines, type MachineDetail } from "@/features/lines/hooks";
import { isDraftLineId } from "@/features/lines/canvas/draft-canvas-adapter";
import { Notify } from "@/components/ui/notify";
import { Header } from "./Header";
import { TabsRail } from "./TabsRail";
import { TabsPanes } from "./TabsPanes";
import { useEscClose } from "@/hooks/useEscClose";
import { useDirtyTabs } from "./hooks/useDirtyTabs";
import type { MachineChangeEvent, SidebarContext, SidebarMode } from "./types";

export type { SidebarMode, SidebarContext, MachineChangeEvent } from "./types";

interface Props {
  machineId: string | null | undefined;
  lineId: string;
  mode: SidebarMode;
  context: SidebarContext;
  onClose: () => void;
  onMachineChange?: (event: MachineChangeEvent) => void;
  onCancel?: (provisoryNodeId?: string) => void;
  provisoryNodeId?: string;
}

export function MachineSidebar({
  machineId,
  lineId,
  mode,
  context,
  onClose,
  onMachineChange,
  onCancel,
  provisoryNodeId,
}: Props) {
  const open = !!machineId || context === "canvas-add";
  const me = useAuthStore((s) => s.user);
  const isOperator = me?.role === "OPERADOR";
  const effectiveMode: SidebarMode = mode === "edit" && isOperator ? "view" : mode;

  const [activeTab, setActiveTab] = useState<string>("basic");
  const { dirtyTabs, markDirty, clear: clearDirty } = useDirtyTabs();

  const compactRail = useMediaQuery("(max-width: 640px)");

  const lineMachinesQuery = useLineMachines(lineId, 30_000);
  const { data: machines = [] } = lineMachinesQuery;
  const machine: MachineDetail | undefined = machineId
    ? machines.find((m) => m.id === machineId)
    : undefined;
  const isCanvasAdd = context === "canvas-add";
  const isCreating = isCanvasAdd && !machineId;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync activeTab quando machine muda — preserva contexto entre maquinas
    setActiveTab("basic");
    clearDirty();
  }, [machineId, clearDirty]);

  async function handleClose() {
    if (dirtyTabs.size > 0) {
      const ok = await Notify.confirm({
        variant: "warning",
        title: "Descartar alterações não salvas?",
        confirmLabel: "Descartar",
      });
      if (!ok) return;
    }
    if (isCreating && onCancel) {
      onCancel(provisoryNodeId);
    }
    onClose();
  }

  useEscClose(open, () => void handleClose(), [dirtyTabs.size]);

  function handleMachineChange(event: MachineChangeEvent) {
    onMachineChange?.(event);
    markDirty(activeTab, false);
    if (event.type === "removed") onClose();
  }

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const showConfigTabs = context !== "dashboard";
  const configTabsDisabled = isCreating;
  const isDraft = isDraftLineId(lineId);

  // Sem isto, erro de fetch faria `machine` ficar undefined e o Header exibiria
  // "Máquina não encontrada" (mensagem enganosa: a máquina existe, só não carregou).
  if (lineMachinesQuery.isError && !isCreating) {
    return createPortal(
      <div className="fixed inset-0 z-40" aria-hidden={!open}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- overlay backdrop da sheet; click fecha via target check */}
        <div className="absolute inset-0 bg-background/60" onClick={() => void handleClose()} />
        <aside
          role="dialog"
          aria-modal="true"
          aria-label="Detalhes da máquina"
          className="absolute right-0 top-0 flex h-full w-full max-w-[632px] flex-col border-l bg-card shadow-2xl"
        >
          <div className="flex items-center justify-between gap-2 border-b p-4">
            <h2 className="text-base font-semibold">Detalhes da máquina</h2>
          </div>
          <div className="flex-1 overflow-auto p-4">
            <Notify.QueryError
              error={lineMachinesQuery.error}
              onRetry={() => void lineMachinesQuery.refetch()}
              title="Não foi possível carregar a máquina"
            />
          </div>
        </aside>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-40" aria-hidden={!open}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- overlay backdrop da sheet; click fecha via target check */}
      <div className="absolute inset-0 bg-background/60" onClick={() => void handleClose()} />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Detalhes da máquina"
        className="absolute right-0 top-0 flex h-full w-full max-w-[632px] flex-col border-l bg-card shadow-2xl"
      >
        <Header isCreating={isCreating} machine={machine} onClose={() => void handleClose()} />

        {effectiveMode !== mode ? (
          <div className="border-b bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
            Permissões insuficientes para edição.
          </div>
        ) : null}

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex min-h-0 flex-1 flex-row"
        >
          <TabsRail
            compactRail={compactRail}
            dirtyTabs={dirtyTabs}
            showConfigTabs={showConfigTabs}
            configTabsDisabled={configTabsDisabled}
            isDraft={isDraft}
          />
          <TabsPanes
            machine={machine}
            lineId={lineId}
            effectiveMode={effectiveMode}
            context={context}
            showConfigTabs={showConfigTabs}
            isDraft={isDraft}
            onMachineChange={handleMachineChange}
            markDirty={markDirty}
          />
        </Tabs>
      </aside>
    </div>,
    document.body
  );
}
